import React from 'react';

type SaleSegment = {
  average?: number | null;
  median?: number | null;
  min?: number | null;
  max?: number | null;
  averagePerSqFt?: number | null;
  medianPerSqFt?: number | null;
  averageSquareFootage?: number | null;
  medianSquareFootage?: number | null;
  averageDaysOnMarket?: number | null;
  medianDaysOnMarket?: number | null;
  minDaysOnMarket?: number | null;
  maxDaysOnMarket?: number | null;
  newListings?: number | null;
  totalListings?: number | null;
};

const money = (n: number | null | undefined, decimals = 0) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const value = Number(n);
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

const num = (n: number | null | undefined, decimals = 0) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const IconCircle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      width: 44,
      height: 44,
      borderRadius: '50%',
      background: '#e6f6f7',
      color: '#1f8a95',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 10px',
    }}
  >
    {children}
  </div>
);

const SnapshotCard: React.FC<{
  title: string;
  value: string;
  accent: string;
  icon: React.ReactNode;
  lines: string[];
}> = ({ title, value, accent, icon, lines }) => (
  <div
    style={{
      background: '#fff',
      borderRadius: 14,
      boxShadow: '0 2px 10px rgba(15, 23, 42, 0.08)',
      border: '1px solid #eef2f7',
      padding: '16px 12px 14px',
      textAlign: 'center',
      minWidth: 0,
    }}
  >
    <IconCircle>{icon}</IconCircle>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2b49', marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: '#1a2b49', lineHeight: 1.15, marginBottom: 8 }}>
      {value}
    </div>
    <div
      style={{
        width: 36,
        height: 3,
        borderRadius: 2,
        background: accent,
        margin: '0 auto 10px',
      }}
    />
    {lines.map((line) => (
      <div key={line} style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.45 }}>
        {line}
      </div>
    ))}
  </div>
);

const HouseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M10 10h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const TagIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 12.5V5.8A1.8 1.8 0 0 1 4.8 4h6.7c.5 0 1 .2 1.3.6l7.4 7.4a1.8 1.8 0 0 1 0 2.5l-5.1 5.1a1.8 1.8 0 0 1-2.5 0L4.6 13.8A1.8 1.8 0 0 1 3 12.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="8.2" cy="8.2" r="1.2" fill="currentColor" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 3.5v3M16 3.5v3M3.5 9.5h17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SignIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3v18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <rect x="5" y="5" width="14" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 9h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const FloorplanIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="3.5" width="17" height="17" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3.5 12h17M12 3.5v8.5M12 15.5V20.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const MarketSnapshotCards: React.FC<{ sale?: SaleSegment | null }> = ({ sale }) => {
  if (!sale) return null;

  const cards = [
    {
      title: 'Median Sale Price',
      value: money(sale.median),
      accent: '#1f8a95',
      icon: <HouseIcon />,
      lines: [
        `Average: ${money(sale.average)}`,
        `Range: ${money(sale.min)} – ${money(sale.max)}`,
      ],
    },
    {
      title: 'Median $/Sq Ft',
      value: money(sale.medianPerSqFt, 2),
      accent: '#2d6cb4',
      icon: <TagIcon />,
      lines: [`Average: ${money(sale.averagePerSqFt, 2)}`],
    },
    {
      title: 'Median DOM',
      value: sale.medianDaysOnMarket != null ? `${num(sale.medianDaysOnMarket)} days` : '—',
      accent: '#1f8a95',
      icon: <CalendarIcon />,
      lines: [
        `Average: ${sale.averageDaysOnMarket != null ? `${num(sale.averageDaysOnMarket, 2)} days` : '—'}`,
        `Range: ${num(sale.minDaysOnMarket)} – ${num(sale.maxDaysOnMarket)}`,
      ],
    },
    {
      title: 'Active Listings',
      value: num(sale.totalListings),
      accent: '#2d6cb4',
      icon: <SignIcon />,
      lines: [`New Listings: ${num(sale.newListings)}`],
    },
    {
      title: 'Median Sq Footage',
      value: sale.medianSquareFootage != null ? `${num(sale.medianSquareFootage)} sq ft` : '—',
      accent: '#1f8a95',
      icon: <FloorplanIcon />,
      lines: [
        `Average: ${sale.averageSquareFootage != null ? `${num(sale.averageSquareFootage)} sq ft` : '—'}`,
      ],
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
      }}
    >
      {cards.map((card) => (
        <SnapshotCard key={card.title} {...card} />
      ))}
    </div>
  );
};

export default MarketSnapshotCards;
